import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import View from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import { Feature } from 'src/replicant/analytics';
import { PetEntity } from 'src/game/logic/EntityManager';
import { PetSpeciesID } from 'src/replicant/pet';
import * as Pet from 'src/replicant/pet';
import { petAnimationNames } from 'src/replicant/pet/PetAssets';
import Match2BuruStreakPanel from './Match2BuruStreakPanel';
import { isMatch2UnlockableUnlocked } from 'src/replicant/match2/match2.getters';
import Match2PinchLossPanel from './Match2PinchLossPanel';
import { user } from 'src/StateObserver';
import { createUIPetCrate } from 'src/game/pet/pet.utils';

import { AssetGroup } from '@play-co/timestep-core';

type CloseHandler = (result: boolean) => void;
const ui = uiConfig.buruburu.care.confirmation;

export default class PopupMatch2Quit extends PopupBasic {
  private contentWrapper: View;
  private pet?: PetEntity;
  private species: PetSpeciesID | undefined;
  private streak?: Match2BuruStreakPanel;

  constructor(opts: { superview: View; close: CloseHandler }) {
    const offset = 140;
    super({
      superview: opts.superview,
      close: () => opts.close(false),
      ...ui.popup,
      height: ui.popup.height - offset,
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.QUIT.POPUP,
      },
    });

    this.buttonClose.removeFromSuperview();
    this.message.removeFromSuperview();
    this.title.updateOpts({ size: 30 });

    this.contentWrapper = new View({
      superview: this.box,
      width: this.box.style.width,
      height: this.box.style.height,
    });

    // Reserve the standard loss-panel slot for specialized quit warnings.
    this.message.updateOpts({
      x: 395,
      y: 205,
      width: 253,
      height: 213,
      centerOnOrigin: true,
      centerAnchor: true,
      size: 26,
      visible: false,
    });
    this.contentWrapper.addSubview(this.message);

    new ButtonScaleViewWithText({
      ...ui.backButton,
      superview: this.contentWrapper,
      y: ui.backButton.y - offset,
      localeText: () => i18n('basic.cancel'),
      onClick: async () => {
        sounds.sfx.play('cancel');
        return opts.close(false);
      },
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.QUIT.CANCEL,
      },
    });

    this.species = Pet.getters.getCurrentEvolution(user())?.species;
    if (!this.species) {
      const petCrate = createUIPetCrate();
      petCrate.updateOpts({
        superview: this.contentWrapper,
        x: 150,
        y: 190,
        scale: 0.7,
        centerOnOrigin: true,
      });
    } else {
      this.pet = new PetEntity('minigame', this.species);
      this.pet.updateOpts({
        superview: this.contentWrapper,
        x: 140,
        y: 300,
        scale: 0.75,
        anchorX: 0.5 * this.pet.style.width,
        anchorY: 0.78 * this.pet.style.height,
      });
    }

    new ButtonScaleViewWithText({
      ...ui.okButton,
      y: ui.okButton.y - offset,
      superview: this.contentWrapper,
      localeText: () => i18n('match2.ui.quitButton'),
      onClick: async () => {
        sounds.sfx.play('ok');
        return opts.close(true);
      },
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.QUIT.CONFIRM,
      },
    });
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      'assets/ui/match2/common/panel_pinch.png',
      'assets/ui/match2/common/icon_tick2.png',
      'assets/ui/match2/common/panel_burustreak.png',
      'assets/ui/match2/common/streak_bg.png',
    ]);
  }

  private createBuruStreakPanel() {
    this.streak = new Match2BuruStreakPanel({
      superview: this.box,
      x: this.box.style.width * 0.5,
      y: this.box.style.height + 190,
    });
  }

  /**
   * Configure the quit confirmation for the active puzzle.
   *
   * @param options Popup content options.
   * @param options.objectivesCount Frenzy objectives earned during this attempt.
   * @param options.showLoseRecovery Whether standard Match-2 loss callouts are shown.
   * @param options.warningMessage Specialized warning replacing standard loss callouts.
   */
  public init({
    objectivesCount = 0,
    showLoseRecovery = true,
    warningMessage,
  }: {
    objectivesCount?: number;
    showLoseRecovery?: boolean;
    warningMessage?: string;
  }) {
    super.init({
      title: i18n('match2.movesPinch.title2'),
      message: warningMessage,
    });

    const petAnimations = petAnimationNames[this.species ?? 'ball'];
    this.pet?.setAnimation(petAnimations.very_sad.idle);

    // Show specialized warning copy only when the standard loss panel is absent.
    this.message.updateOpts({
      visible: !showLoseRecovery && !!warningMessage,
    });

    // Specialized puzzle modes can use the generic quit confirmation only.
    if (!showLoseRecovery) return;

    // Show the standard Match-2 progress that will be lost by quitting.
    new Match2PinchLossPanel({
      superview: this.contentWrapper,
      objectivesCount,
      x: 395,
      y: 205,
    });

    // Show the extended Buru Streak progress when the feature is unlocked.
    if (isMatch2UnlockableUnlocked(user(), 'buruStreak')) {
      this.createBuruStreakPanel();
    }
  }

  public override async fadeIn() {
    await super.fadeIn();
    await this.streak?.show();
    this.streak?.flashProgress();
  }

  public override async fadeOut() {
    this.streak?.hide?.();
    await super.fadeOut();
    this.streak?.unflashProgress();
  }
}
